import { faBriefcase, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { CreateCaseSourceState } from "src/cases/createCase/createCaseSteps";
import { useCaseQueues } from "src/cases/queries/queues";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { resolveVersion } from "src/cases/queries/useResolvedTemplateVersion";
import { SelectQueueModal } from "src/cases/SelectQueueModal";
import { Button } from "src/design-system/Button";
import { useModal } from "src/design-system/Modal";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { DashboardPageParams, getUrlToCreateCasePage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const useCreateCaseEligibility = () => {
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();
  const { cases } = useCapabilities();
  const { data: allQueues = [] } = useCaseQueues();
  const { data: templates } = useCaseTemplatesWithVersions();

  const queues = allQueues.filter((queue) => {
    const templateId = queue.default_template_version?.template?.id;
    const templateWithVersions = templates?.find((t) => t.id === templateId);
    const { status } = resolveVersion(
      queue.default_template_version,
      templateWithVersions,
    );
    return status === "ok";
  });

  const canCreateCase =
    isCaseManagementEnabled && cases.canCreate && queues.length > 0;

  return { canCreateCase, queues };
};

type CreateCaseActionsDropdownProps = {
  sourceState: CreateCaseSourceState;
  dataLoc: string;
  variant?: "ellipsis" | "button";
  onCreateCaseClick?: () => void;
};

export const CreateCaseActionsDropdown: React.FC<
  CreateCaseActionsDropdownProps
> = ({ sourceState, dataLoc, variant = "ellipsis", onCreateCaseClick }) => {
  const { canCreateCase, queues } = useCreateCaseEligibility();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const { isOpen, openModal, closeModal, afterLeave } = useModal();

  if (!canCreateCase) {
    return null;
  }

  const handleCreateCase = () => {
    onCreateCaseClick?.();
    if (queues.length === 1) {
      navigate(getUrlToCreateCasePage({ orgId, wsId, queueId: queues[0].id }), {
        state: sourceState,
      });
    } else {
      openModal();
    }
  };

  const handleSelectQueue = (queueId: string) => {
    navigate(getUrlToCreateCasePage({ orgId, wsId, queueId }), {
      state: sourceState,
    });
  };

  const elements: OptionsDropdownElement[] = [
    {
      key: "Create a Case",
      icon: faBriefcase,
      action: handleCreateCase,
    },
  ];

  return (
    <>
      {variant === "button" ? (
        <Button
          dataLoc={dataLoc}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={handleCreateCase}
        >
          Create Case
        </Button>
      ) : (
        <EllipsisOptionsDropdown
          dataLoc={dataLoc}
          elements={elements}
          iconSize="sm"
          placement="bottom end"
        />
      )}
      <SelectQueueModal
        afterLeave={afterLeave}
        open={isOpen}
        queues={queues}
        sourceSchemaId={sourceState.sourceSchemaId}
        sourceType={sourceState.sourceType}
        onClose={closeModal}
        onSelectQueue={handleSelectQueue}
      />
    </>
  );
};
